import React, { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function Compare({ rows }) {
  return (
    <table className="w-full text-xs mt-2" data-testid="recon-compare">
      <thead><tr className="text-[#9E978E] text-left"><th className="py-1">Check</th><th>Expected</th><th>Actual</th></tr></thead>
      <tbody>{rows.map((c, i) => (
        <tr key={i} className={String(c.expected) !== String(c.actual) ? "text-[#E58CA0]" : "text-[#D8D2C9]"}>
          <td className="py-1 pr-2">{c.check}</td><td>{String(c.expected)}</td><td>{String(c.actual)}</td></tr>))}
      </tbody>
    </table>
  );
}

function Detail({ t, run, onDone }) {
  const [d, setD] = useState(null);
  const [action, setAction] = useState("annotate");
  const [note, setNote] = useState("");
  const [pv, setPv] = useState(null);
  useEffect(() => { api.get(`/admin/reconciliation/${t.type}/${encodeURIComponent(t.id)}`).then((r) => setD(r.data)); }, [t]);
  const preview = async () => {
    try { setPv((await api.post("/admin/reconciliation/preview", { target_type: t.type, target_id: t.id, action, note })).data); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || "Preview failed"); }
  };
  const apply = () => run(async () => {
    await api.post("/admin/reconciliation/apply", { preview_token: pv.preview_token, confirm: "APPLY" }); setPv(null); onDone();
  }, "Correction recorded");
  if (!d) return <div className="text-xs text-[#9E978E]">Loading…</div>;
  return (
    <div className="rounded-2xl border border-white/8 bg-[#141113]/70 p-4 space-y-3" data-testid="recon-detail">
      <Compare rows={d.comparison} />
      {d.issues?.length > 0 && <ul className="text-xs text-[#E58CA0]" data-testid="recon-issues">{d.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>}
      <div className="flex flex-col sm:flex-row gap-2">
        <select data-testid="recon-action" value={action} onChange={(e) => { setAction(e.target.value); setPv(null); }}
          className="rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3]">
          <option value="annotate">Add reconciliation note (no balance change)</option>
          <option value="release_freeze">Release frozen purchase</option>
          <option value="adjust_balance">Adjust balance (needs owner approval)</option>
        </select>
        <input data-testid="recon-note" value={note} onChange={(e) => { setNote(e.target.value); setPv(null); }} placeholder="Reason (min 10 characters)"
          className="flex-1 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3]" />
        <button data-testid="recon-preview-btn" disabled={note.trim().length < 10} onClick={preview}
          className="rounded-full border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2 text-sm disabled:opacity-40">Dry-run preview</button>
      </div>
      {pv && (
        <div className="rounded-xl bg-black/30 p-3 text-xs space-y-2" data-testid="recon-preview">
          {pv.plan.allowed
            ? <><div className="text-[#D8D2C9]">Planned: {pv.plan.operations.join(" · ")}</div>
                <button data-testid="recon-apply-btn" onClick={apply} className="rounded-full bg-[#D4AF37] text-[#0C0A0A] px-4 py-1.5 font-medium">Confirm and apply</button></>
            : <div className="text-[#E5A84B]" data-testid="recon-blocked">Blocked: {pv.plan.blocked_reason}</div>}
        </div>)}
    </div>
  );
}

export default function AdminReconciliation({ run }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/reconciliation/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-[#9E978E]">Scanning…</div>;
  const items = [
    ...data.accounts_with_findings.map((a) => ({ type: "account", id: a.user_id, findings: a.findings })),
    ...data.purchases_with_findings.map((p) => ({ type: "purchase", id: p.session_id, findings: p.findings })),
  ];
  return (
    <div className="space-y-4" data-testid="admin-reconciliation">
      <p className="text-sm text-[#9E978E] flex gap-2"><Scale size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
        Read-only comparison of balances, credit lots, ledger, reversals and payments. Nothing is corrected automatically; any correction needs a dry-run preview and a fresh admin code.</p>
      <div className="text-xs text-[#9E978E]" data-testid="recon-summary">Accounts scanned: {data.users_scanned} · Paid purchases: {data.paid_purchases_scanned} · Items: {items.length}</div>
      {items.map((it) => (
        <div key={it.type + it.id} className="space-y-2">
          <button data-testid={`recon-item-${it.id}`} onClick={() => setSel(sel?.id === it.id ? null : it)}
            className="w-full text-left rounded-xl border border-white/8 px-4 py-3 hover:bg-[#541224]/30 transition-colors">
            <span className="text-xs uppercase text-[#D4AF37] mr-2">{it.type}</span>
            <span className="font-mono text-xs text-[#FBF8F3]">{it.id}</span>
            <span className="block text-xs text-[#B5AEA4] mt-1">{it.findings.map((f) => `${f.sev}: ${f.issue}`).join(" · ")}</span>
          </button>
          {sel?.id === it.id && <Detail t={sel} run={run} onDone={load} />}
        </div>))}
    </div>
  );
}
